import { useEffect, useRef, useState, type RefObject } from 'react'
import type { IChartApi, ISeriesApi, MouseEventParams } from 'lightweight-charts'
import { fromLocalChartTime } from './chartTime'

export interface ChartPin {
  value: number
  time: number
  x: number
}

export interface ChartSelection {
  start: ChartPin
  end: ChartPin
}

interface UseChartDragSelectionParams {
  wrapRef: RefObject<HTMLDivElement | null>
  chartRef: RefObject<IChartApi | null>
  seriesRef: RefObject<ISeriesApi<'Area'> | null>
  /**
   * Re-attach the crosshair handler when this changes. Pass the current data
   * array (or any identity-changing value) so the handler re-binds whenever
   * the chart receives new data.
   */
  resubscribeKey?: unknown
}

interface UseChartDragSelectionResult {
  hovered: ChartPin | null
  selection: ChartSelection | null
  inSelection: boolean
  selLeft: ChartPin | null
  selRight: ChartPin | null
  selDelta: number
  selDeltaPct: number
  selIsUp: boolean
}

/**
 * Wires click-and-drag range selection on a lightweight-charts area series.
 * Mousedown on the wrapper anchors the most recent hovered pin as the start;
 * subsequent crosshair moves extend the selection. Releasing the mouse clears
 * it. While dragging, `hovered` does not update (so the chart can keep showing
 * a stable selection-end pin in its header).
 */
export function useChartDragSelection({
  wrapRef,
  chartRef,
  seriesRef,
  resubscribeKey,
}: UseChartDragSelectionParams): UseChartDragSelectionResult {
  const [hovered, setHovered] = useState<ChartPin | null>(null)
  const [selection, setSelection] = useState<ChartSelection | null>(null)
  const dragStartRef = useRef<ChartPin | null>(null)
  // Mirrors `hovered` but is updated synchronously inside the crosshair-move
  // handler — mousedown reads from this ref so it can't lag behind the latest
  // hover position (React effects commit after the event loop, too late).
  const hoveredRef = useRef<ChartPin | null>(null)

  useEffect(() => {
    const chart = chartRef.current
    const series = seriesRef.current
    if (!chart || !series) return
    const handler = (param: MouseEventParams) => {
      if (!param.point || param.point.x < 0 || param.time == null) {
        if (!dragStartRef.current) {
          hoveredRef.current = null
          setHovered(null)
        }
        return
      }
      const data = param.seriesData.get(series) as { value?: number } | undefined
      if (data?.value == null) {
        if (!dragStartRef.current) {
          hoveredRef.current = null
          setHovered(null)
        }
        return
      }
      const timeMs =
        typeof param.time === 'number'
          ? fromLocalChartTime(param.time as number)
          : Date.parse(String(param.time))
      const pin: ChartPin = {
        value: data.value,
        time: Number.isFinite(timeMs) ? timeMs : Date.now(),
        x: param.point.x,
      }
      if (dragStartRef.current) {
        setSelection({ start: dragStartRef.current, end: pin })
      } else {
        hoveredRef.current = pin
        setHovered(pin)
      }
    }
    chart.subscribeCrosshairMove(handler)
    return () => chart.unsubscribeCrosshairMove(handler)
    // chartRef/seriesRef are stable, but we resubscribe when the caller's data
    // changes (which can happen with the same chart instance).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resubscribeKey])

  useEffect(() => {
    const wrap = wrapRef.current
    if (!wrap) return

    function onMouseDown(e: MouseEvent) {
      if (e.button !== 0) return
      const anchor = hoveredRef.current
      if (!anchor) return
      dragStartRef.current = anchor
      setSelection({ start: anchor, end: anchor })
      e.preventDefault()
    }

    function onMouseUp() {
      dragStartRef.current = null
      setSelection(null)
    }

    wrap.addEventListener('mousedown', onMouseDown)
    window.addEventListener('mouseup', onMouseUp)
    return () => {
      wrap.removeEventListener('mousedown', onMouseDown)
      window.removeEventListener('mouseup', onMouseUp)
    }
  }, [wrapRef])

  const inSelection = selection != null
  const selLeft = selection
    ? selection.start.time <= selection.end.time
      ? selection.start
      : selection.end
    : null
  const selRight = selection
    ? selection.start.time <= selection.end.time
      ? selection.end
      : selection.start
    : null
  const selStartValue = selLeft?.value ?? 0
  const selEndValue = selRight?.value ?? 0
  const selDelta = selEndValue - selStartValue
  const selDeltaPct = selStartValue > 0 ? (selDelta / selStartValue) * 100 : 0
  const selIsUp = selDelta >= 0

  return {
    hovered,
    selection,
    inSelection,
    selLeft,
    selRight,
    selDelta,
    selDeltaPct,
    selIsUp,
  }
}
